/**
 * UserWebSocket Context
 *
 * Provides a global user-level WebSocket connection that:
 * - Connects once per user session (not per stream)
 * - Allows subscribing to multiple streams via messages
 * - Enables instant stream switching (no TCP/TLS teardown)
 * - Delivers cross-stream notifications in real-time
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { UserWebSocket, type WebSocketStatus } from '../lib/userWebSocket';
import { useAuth } from './AuthContext';
import type { StreamEvent, ObservabilityEvent } from '../types/api';
import { getCacheLastSeq } from '../lib/streamCache';
import { axiom } from '../lib/axiom';
import { DeployReconnectToast } from '../components/errors/DeployReconnectToast';

interface UserWebSocketContextType {
  status: WebSocketStatus;
  error: Error | null;
  errorStreamId: string | null;  // Stream ID associated with the error (for scoping)
  /** Warning message (e.g., subscription degraded) */
  warning: { code: string; message: string; streamId?: string } | null;
  /** Whether we're in a deploy reconnect cycle */
  isDeployReconnecting: boolean;
  /** Current deploy reconnect attempt (1-based) */
  deployReconnectAttempt: number;
  subscribe: (streamId: string) => Promise<void>;
  unsubscribe: (streamId: string) => void;
  sendMessage: (streamId: string, message: string, nodeId?: string) => boolean;
  approveToolCall: (streamId: string, toolCallId: string) => void;
  rejectToolCall: (streamId: string, toolCallId: string, reason?: string) => void;
  cancelAgentLoop: (streamId: string, nodeId?: string) => void;
  // enableObservability/disableObservability removed (Spec 167 Phase 2: always-on)
  isSubscribed: (streamId: string) => boolean;
  getLastSeq: (streamId: string) => number;
  // Direct access for hooks that need event registration
  userWebSocket: UserWebSocket | null;
}

const UserWebSocketContext = createContext<UserWebSocketContextType | null>(null);

interface UserWebSocketProviderProps {
  children: React.ReactNode;
  onStreamEvent?: (streamId: string, event: StreamEvent) => void;
  onHardRefreshRequired?: (streamId: string) => void;
  /** Called when an observability event is received (Glass Box UI, Spec 124 Phase 1b) */
  onObservabilityEvent?: (streamId: string, event: ObservabilityEvent) => void;
  /**
   * Enable test mode - bypasses Firebase auth and uses test_email query param.
   * Only works when backend is running with DEV_MODE=true.
   * @see docs/specs/044-integration-testing-framework.md
   */
  testMode?: boolean;
  /**
   * Test user email - required when testMode is true.
   * Sent to backend via test_email query param on WebSocket URL.
   */
  testEmail?: string;
  /** Expose the socket on window for E2E assertions without changing auth. */
  exposeForTests?: boolean;
}

export function UserWebSocketProvider({
  children,
  onStreamEvent,
  onHardRefreshRequired,
  onObservabilityEvent,
  testMode = false,
  testEmail,
  exposeForTests = false,
}: UserWebSocketProviderProps) {
  const { user, loading: authLoading } = useAuth();

  // In test mode, bypass auth check
  // Guard: testEmail must be provided when testMode is true
  if (testMode && !testEmail) {
    throw new Error('testEmail is required when testMode is true');
  }

  // Stabilize effectiveUser to prevent reconnect loops
  // Use user?.email as dependency for production mode, testEmail for test mode
  const effectiveUser = useMemo(
    () => (testMode ? { email: testEmail } : user),
    [testMode, testEmail, user]
  );
  const effectiveLoading = testMode ? false : authLoading;
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const [errorStreamId, setErrorStreamId] = useState<string | null>(null);
  const [warning, setWarning] = useState<{ code: string; message: string; streamId?: string } | null>(null);
  const [isDeployReconnecting, setIsDeployReconnecting] = useState(false);
  const [deployReconnectAttempt, setDeployReconnectAttempt] = useState(0);
  const [deployMaxAttempts, setDeployMaxAttempts] = useState(5);
  const wsRef = useRef<UserWebSocket | null>(null);

  // Track pending subscription to prevent stale async operations
  const pendingSubscribeRef = useRef<string | null>(null);

  // Store callbacks in refs to avoid recreating effects
  const onStreamEventRef = useRef(onStreamEvent);
  const onHardRefreshRequiredRef = useRef(onHardRefreshRequired);
  const onObservabilityEventRef = useRef(onObservabilityEvent);

  useEffect(() => {
    onStreamEventRef.current = onStreamEvent;
  }, [onStreamEvent]);

  useEffect(() => {
    onHardRefreshRequiredRef.current = onHardRefreshRequired;
  }, [onHardRefreshRequired]);

  useEffect(() => {
    onObservabilityEventRef.current = onObservabilityEvent;
  }, [onObservabilityEvent]);

  // Connect when user is authenticated (or in test mode)
  useEffect(() => {
    if (effectiveLoading) {
      return;
    }

    if (effectiveUser) {
      // Only create a new WebSocket if we don't already have one
      // This prevents creating multiple instances when useEffect re-runs
      if (wsRef.current) {
        axiom.info('[UserWebSocketContext] WebSocket already exists, skipping creation');
        return;
      }

      axiom.info('[UserWebSocketContext] User authenticated, connecting...', { testMode });
      const ws = new UserWebSocket(testMode ? testEmail : undefined);
      wsRef.current = ws;

      ws.on('statusChange', (newStatus) => {
        axiom.info(`[UserWebSocketContext] Status changed: ${newStatus}`);
        setStatus(newStatus);
        // Clear error, warning, and deploy state on successful connection
        if (newStatus === 'connected') {
          setError(null);
          setErrorStreamId(null);
          setWarning(null);
          setIsDeployReconnecting(false);
          setDeployReconnectAttempt(0);
        }
      });

      ws.on('streamEvent', (streamId: string, event: StreamEvent) => {
        axiom.info('[UserWebSocketContext] Stream event', { stream_id: streamId.slice(0, 8), event_type: event.type });
        onStreamEventRef.current?.(streamId, event);
      });

      ws.on('subscribed', (streamId: string, lastSeq: number) => {
        axiom.info(`[UserWebSocketContext] Subscribed to ${streamId.slice(0, 8)} at seq ${lastSeq}`);
      });

      ws.on('error', (err: { code: string; message: string; recoverable?: boolean }, errStreamId?: string) => {
        axiom.error('[UserWebSocketContext] Error', { code: err.code, message: err.message, stream_id: errStreamId });
        // Convert structured error to Error object for backward compatibility with existing error state
        setError(new Error(`${err.code}: ${err.message}`));
        setErrorStreamId(errStreamId ?? null);
      });

      ws.on('hardRefreshRequired', (streamId: string) => {
        axiom.info(`[UserWebSocketContext] Hard refresh required for ${streamId.slice(0, 8)}`);
        onHardRefreshRequiredRef.current?.(streamId);
      });

      ws.on('warning', (code: string, message: string, streamId?: string) => {
        axiom.warn(`[UserWebSocketContext] Warning received`, { code, message, stream_id: streamId });
        setWarning({ code, message, streamId });
      });

      ws.on('observabilityEvent', (streamId: string, event: ObservabilityEvent) => {
        onObservabilityEventRef.current?.(streamId, event);
      });

      ws.on('deployReconnecting', (attempt: number, maxAttempts: number) => {
        axiom.info(`[UserWebSocketContext] Deploy reconnecting`, { attempt, max_attempts: maxAttempts });
        setIsDeployReconnecting(true);
        setDeployReconnectAttempt(attempt);
        setDeployMaxAttempts(maxAttempts);
      });

      ws.on('deployReconnectFailed', () => {
        axiom.warn(`[UserWebSocketContext] Deploy reconnect failed - exhausted attempts`);
        setIsDeployReconnecting(false);
        setDeployReconnectAttempt(0);
        // Trigger HardRefreshModal after exhausting deploy reconnect attempts
        setError(new Error('hard-refresh-required'));
      });

      ws.connect().catch((err) => {
        axiom.error('[UserWebSocketContext] Connect failed', { error: err instanceof Error ? err.message : String(err) });
      });

      // Expose WebSocket to window for integration assertions. Production
      // E2E uses normal ticket auth, so exposure is independent of testMode.
      // Use wsRef.current getter to always point to the current instance
      if (testMode || exposeForTests) {
        (window as unknown as { __userWebSocket?: { ws: WebSocket | null; userWebSocket: UserWebSocket | null } }).__userWebSocket = {
          get ws() {
            return wsRef.current?.getRawWebSocket() ?? null;
          },
          get userWebSocket() {
            return wsRef.current;
          },
        };
      }

      // Ping to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.getStatus() === 'connected') {
          ws.ping();
        }
      }, 30000);

      return () => {
        clearInterval(pingInterval);
        ws.disconnect();
        wsRef.current = null;
        if (testMode || exposeForTests) {
          delete (window as unknown as { __userWebSocket?: unknown }).__userWebSocket;
        }
      };
    } else {
      // No user - disconnect
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }
      setStatus('disconnected');
    }
  }, [effectiveUser, effectiveLoading, testMode, testEmail, exposeForTests]);

  /**
   * Subscribe to a stream's events.
   * Automatically uses cached lastSeq for delta sync.
   * Guards against stale subscriptions from fast stream switching.
   */
  const subscribe = useCallback(async (streamId: string): Promise<void> => {
    const ws = wsRef.current;
    if (!ws) {
      axiom.warn('[UserWebSocketContext] Cannot subscribe - not connected');
      return;
    }

    // Mark this as the pending subscription
    pendingSubscribeRef.current = streamId;

    // Get lastSeq from cache for delta sync (async operation)
    const lastSeq = await getCacheLastSeq(streamId);

    // Guard: check if this subscription is still the pending one
    // If user switched streams during the async lookup, abort
    if (pendingSubscribeRef.current !== streamId) {
      axiom.info(`[UserWebSocketContext] Skipping stale subscribe for ${streamId.slice(0, 8)} (current: ${pendingSubscribeRef.current?.slice(0, 8)})`);
      return;
    }

    ws.subscribe(streamId, lastSeq);
  }, []);

  /**
   * Unsubscribe from a stream's events.
   * Also cancels any pending subscribe for this stream.
   */
  const unsubscribe = useCallback((streamId: string): void => {
    // Cancel any pending subscribe for this stream
    if (pendingSubscribeRef.current === streamId) {
      pendingSubscribeRef.current = null;
    }

    const ws = wsRef.current;
    if (!ws) {
      return;
    }
    ws.unsubscribe(streamId);
  }, []);

  /**
   * Send a message to a stream.
   */
  const sendMessage = useCallback((streamId: string, message: string, nodeId?: string): boolean => {
    const ws = wsRef.current;
    if (!ws) {
      axiom.warn('[UserWebSocketContext] Cannot send message - not connected');
      return false;
    }
    return ws.sendMessage(streamId, message, nodeId);
  }, []);

  /**
   * Check if currently subscribed to a stream.
   */
  const isSubscribed = useCallback((streamId: string): boolean => {
    return wsRef.current?.isSubscribed(streamId) || false;
  }, []);

  /**
   * Get the last sequence number for a stream.
   */
  const getLastSeq = useCallback((streamId: string): number => {
    return wsRef.current?.getLastSeq(streamId) || 0;
  }, []);

  /**
   * Approve a pending tool call.
   */
  const approveToolCall = useCallback((streamId: string, toolCallId: string): void => {
    wsRef.current?.approveToolCall(streamId, toolCallId);
  }, []);

  /**
   * Reject a pending tool call.
   */
  const rejectToolCall = useCallback((streamId: string, toolCallId: string, reason?: string): void => {
    wsRef.current?.rejectToolCall(streamId, toolCallId, reason);
  }, []);

  /**
   * Cancel the current agent loop.
   */
  const cancelAgentLoop = useCallback((streamId: string, nodeId?: string): void => {
    wsRef.current?.cancelAgentLoop(streamId, nodeId);
  }, []);

  /**
   * Enable Glass Box observability for a stream (Spec 124 Phase 1b).
   */
  // enableObservability/disableObservability removed (Spec 167 Phase 2: always-on)

  const value: UserWebSocketContextType = {
    status,
    error,
    errorStreamId,
    warning,
    isDeployReconnecting,
    deployReconnectAttempt,
    subscribe,
    unsubscribe,
    sendMessage,
    approveToolCall,
    rejectToolCall,
    cancelAgentLoop,
    isSubscribed,
    getLastSeq,
    userWebSocket: wsRef.current,
  };

  return (
    <UserWebSocketContext.Provider value={value}>
      {children}
      <DeployReconnectToast
        isVisible={isDeployReconnecting}
        attempt={deployReconnectAttempt}
        maxAttempts={deployMaxAttempts}
      />
    </UserWebSocketContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserWebSocket() {
  const context = useContext(UserWebSocketContext);
  if (!context) {
    throw new Error('useUserWebSocket must be used within a UserWebSocketProvider');
  }
  return context;
}
